import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import '../styles/productsAll.css';
import ScrollToTopButton from '../components/ScrollToTopButton';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import ProductsGrid from '../components/ProductsGrid';
import ProductFilters from '../components/ProductFilters';
import PremiumCarousel from '../components/PremiumCarousel';
import { CarouselSkeleton } from '../components/Skeletons';
import { handleLikeClick } from '../utils/likeHandler';
import { ProductGridSkeleton } from '../components/Skeletons';
import PerformanceIndicator from '../components/PerformanceIndicator';
import { PerformanceToggle } from '../components/PerformanceDashboard';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import ProductService from '../services/productService';
import { CategoryContext } from '../contexts/CategoryContext';
import { filterPromotedProducts } from '../utils/promotionUtils';
import { CityContext } from '../contexts/CityContext';
import { UserContext } from '../contexts/UserContext';

const ProductsProvince = () => {
    const { categoryList } = useContext(CategoryContext); // Add CategoryContext
    const { redirectToLogin } = useContext(UserContext);
    const [products, setProducts] = useState([]);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [likedProducts, setLikedProducts] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [currentProvince, setCurrentProvince] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [promotedProducts, setPromotedProducts] = useState([]);
    const [loadingPromoted, setLoadingPromoted] = useState(true);
    const [userProvince, setUserProvince] = useState(null);
    
    // Filter state
    const [filters, setFilters] = useState({
        sortBy: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        city: '',
        negotiable: false,
        promotedOnly: false
    });
    const [allProducts, setAllProducts] = useState([]); // Store unfiltered products
    const [filteredProducts, setFilteredProducts] = useState([]);
    
    // View mode state - category or list
    const [viewMode, setViewMode] = useState('category');
    
    // Performance monitoring
    const { startTiming, endTiming, recordPageLoad } = usePerformanceMonitoring();
    const [performanceData, setPerformanceData] = useState({ loadTime: 0, fromCache: false, itemCount: 0 });

    // Filter products based on current filters
    const applyFilters = useCallback((productsToFilter, currentFilters) => {
        let filtered = [...productsToFilter];

        // Condition filter
        if (currentFilters.condition) {
            filtered = filtered.filter(product => 
                product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
            );
        }

        // Price range filter
        if (currentFilters.minPrice || currentFilters.maxPrice) {
            filtered = filtered.filter(product => {
                const price = parseFloat(product.price) || 0;
                const min = parseFloat(currentFilters.minPrice) || 0;
                const max = parseFloat(currentFilters.maxPrice) || Infinity;
                return price >= min && (max === Infinity || price <= max);
            });
        }

        // City filter
        if (currentFilters.city) {
            filtered = filtered.filter(product => 
                product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
            );
        }

        // Negotiable filter
        if (currentFilters.negotiable) {
            filtered = filtered.filter(product => product.negotiable === true);
        }

        // Promoted only filter
        if (currentFilters.promotedOnly) {
            filtered = filterPromotedProducts(filtered);
        }

        // Sort products
        if (currentFilters.sortBy) {
            switch (currentFilters.sortBy) {
                case 'newest':
                    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'price_low':
                    filtered.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
                    break;
                case 'price_high':
                    filtered.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
                    break;
                case 'most_liked':
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                case 'most_viewed':
                    filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
                    break;
                default:
                    break;
            }
        }

        return filtered;
    }, []);

    // Handle filter changes
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Apply filters when filters or allProducts change
    useEffect(() => {
        if (allProducts.length > 0) {
            const filtered = applyFilters(allProducts, filters);
            setFilteredProducts(filtered);
            if (viewMode === 'list') {
                setProducts(filtered); // Update products displayed in list view
            } else {
                // Update categoryProducts for category view
                const groupedByCategory = groupProductsByCategory(filtered);
                setCategoryProducts(groupedByCategory);
            }
        }
    }, [filters, allProducts, applyFilters, viewMode]);

    // Get cities from context
    const { cities: contextCities, isLoading: citiesLoading } = useContext(CityContext);

    const navigate = useNavigate();

    useEffect(() => {
        const getLoggedInUser = async () => {
            const token = Cookies.get('authToken');
            if (token) {
                try {
                    const response = await fetch(`${config.backendUrl}/users/me/`, {
                        headers: {
                            Authorization: `Token ${token}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setLoggedInUser(data);
                    } else {
                        console.error('Failed to fetch logged-in user');
                    }
                } catch (error) {
                    console.error('Error fetching logged-in user:', error);
                }
            }
        };

        const getInitialData = async () => {
            setLoading(true);
            const pageStartTime = startTiming('page_load');
            const loadStartTime = startTiming('province_products');
            
            try {
                const token = Cookies.get('authToken');
                if (!token) {
                    console.error('No auth token found - redirecting to login');
                    // Redirect to login or show login required message
                    setNotification({
                        message: 'Vous devez être connecté pour voir les produits de votre province.',
                        type: 'info',
                        header: 'Connexion Requise'
                    });
                    setLoading(false);
                    // Optionally redirect to login after a delay
                    setTimeout(() => {
                        redirectToLogin('/login');
                    }, 3000);
                    return;
                }

                // Use the optimized service for province products
                const result = await ProductService.getUserProvinceCombined('', 1, 30);
                const loadTime = endTiming(result.fromCache, result.totalProducts || 0);
                
                // Get the actual products from categories or use result.products directly
                let allProducts = [];
                if (result.categories && Array.isArray(result.categories)) {
                    // If categories exist, extract products from all categories
                    allProducts = result.categories.flatMap(category => category.products || []);
                } else if (result.products && Array.isArray(result.products)) {
                    // If products are returned directly
                    allProducts = result.products;
                }
                
                setAllProducts(allProducts); // Store unfiltered products
                setFilteredProducts(allProducts); // Initialize filtered products
                setProducts(allProducts);
                
                // Group products by category for category view
                const groupedByCategory = groupProductsByCategory(allProducts);
                setCategoryProducts(groupedByCategory);
                
                setCities(result.cities || []);
                setCurrentProvince(result.current_province || '');
                setHasMore(result.hasMore || false);
                setPage(1);
                
                // Merge cities from API with context cities for better coverage
                const apiCities = Array.isArray(result.cities) ? result.cities : [];
                const allCities = [...apiCities];
                
                // Add cities from context that might not be in API response
                if (contextCities && Array.isArray(contextCities) && contextCities.length > 0) {
                    contextCities.forEach(contextCity => {
                        // Only add if not already present in API cities
                        if (!apiCities.find(apiCity => apiCity.id === contextCity.id)) {
                            allCities.push(contextCity);
                        }
                    });
                }
                
                setCities(allCities);
                
                // If we have a current province but still no cities, try to load cities for this province
                if (result.current_province && allCities.length === 0) {
                    try {
                        // Try to get province ID first - we might need to get provinces list first
                        const provincesResult = await ProductService.getPublicProvinces();
                        const currentProvinceObj = provincesResult.data?.find(p => 
                            p.name?.toLowerCase() === result.current_province?.toLowerCase()
                        );
                        
                        if (currentProvinceObj) {
                            const citiesResult = await ProductService.getCitiesByProvince(currentProvinceObj.id);
                            const provinceCities = citiesResult.data || [];
                            setCities(prevCities => [...prevCities, ...provinceCities]);
                        }
                    } catch (error) {

                    }
                }
                
                setPerformanceData({
                    loadTime,
                    fromCache: result.fromCache,
                    itemCount: allProducts.length
                });
            } catch (error) {
                console.error('Error loading province data:', error);
                endTiming(false, 0, error);
                setProducts([]);
                setCities([]);
                setHasMore(false);
                
                // Show error notification
                setNotification({
                    message: 'Erreur lors du chargement des produits de votre province. Veuillez réessayer.',
                    type: 'error',
                    header: 'Erreur'
                });
            } finally {
                setLoading(false);
                const totalPageTime = endTiming(false, 0);
                recordPageLoad('ProductsProvince', totalPageTime);
            }
        };

        getLoggedInUser();
        getInitialData();
    }, []);

    useEffect(() => {
        if (loggedInUser) {
            const liked = products
                .filter(product => product.liked_by && product.liked_by.includes(loggedInUser.id))
                .map(product => product.id);
            setLikedProducts(liked);
        }
    }, [products, loggedInUser]);

    useEffect(() => {
        // Since we're filtering at the API level, we can use all products directly
        setFilteredProducts(products);
    }, [products]);

    // Fetch promoted products by province
    useEffect(() => {
        const fetchPromotedProducts = async () => {
            setLoadingPromoted(true);
            try {
                const token = Cookies.get('authToken');
                if (!token) {
                    setPromotedProducts([]);
                    setLoadingPromoted(false);
                    return;
                }

                // Get user's province ID first
                const userResponse = await fetch(`${config.backendUrl}/users/me/`, {
                    headers: {
                        Authorization: `Token ${token}`,
                    },
                });

                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    setUserProvince(userData.city?.province);
                    
                    if (userData.city?.province?.id) {
                        const response = await fetch(`${config.backendUrl}/products/promoted/province/${userData.city.province.id}/`, {
                            method: 'GET',
                        });

                        if (response.ok) {
                            const data = await response.json();
                            setPromotedProducts(data.results || []);
                        } else {
                            console.error('Failed to fetch province promoted products');
                            setPromotedProducts([]);
                        }
                    } else {
                        setPromotedProducts([]);
                    }
                } else {
                    setPromotedProducts([]);
                }
            } catch (err) {
                console.error('Error fetching province promoted products:', err);
                setPromotedProducts([]);
            } finally {
                setLoadingPromoted(false);
            }
        };

        fetchPromotedProducts();
    }, []);

    // Fetch campaigns with caching
    useEffect(() => {
        const fetchCampaigns = async () => {
            const CAMPAIGNS_CACHE_KEY = 'wennze_campaigns_cache_v2';
            const CAMPAIGNS_CACHE_TTL = 60 * 60 * 1000; // 1 hour

            try {
                // Check cache first
                const cached = localStorage.getItem(CAMPAIGNS_CACHE_KEY);
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < CAMPAIGNS_CACHE_TTL) {
                        setCampaigns(data);
                        return;
                    }
                }

                // Fetch from API if cache is stale or missing
                const response = await fetch(`${config.backendUrl}/campaigns/active/`);
                if (response.ok) {
                    const data = await response.json();
                    
                    // Handle both paginated and non-paginated responses
                    const campaignsArray = Array.isArray(data) ? data : (data.results || []);
                    
                    // Format campaigns for CampaignCard
                    const formattedCampaigns = campaignsArray.map(campaign => ({
                        imageUrl: campaign.images && campaign.images.length > 0 
                            ? campaign.images[0].image 
                            : null,
                        link: campaign.link,
                        caption: {
                            title: campaign.title || 'Campaign',
                            text: campaign.description || ''
                        },
                        ownerId: campaign.user
                    }));

                    setCampaigns(formattedCampaigns);

                    // Cache the results
                    localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
                        data: formattedCampaigns,
                        timestamp: Date.now()
                    }));
                }
            } catch (error) {
                console.error('Error fetching campaigns:', error);
            }
        };

        fetchCampaigns();
    }, []);

    // Effect to ensure cities are available from context when they load
    useEffect(() => {
        if (contextCities && contextCities.length > 0 && cities.length === 0) {
            setCities(contextCities);
        }
    }, [contextCities, cities.length]);

    const handleCityChange = async (event) => {
        const cityId = event.target.value;
        setSelectedCity(cityId);
        setLoading(true);
        setPage(1);
        const loadStartTime = startTiming('city_filter');
        
        try {
            const result = await ProductService.getUserProvinceCombined(cityId, 1, 30);
            const loadTime = endTiming(result.fromCache, result.totalProducts || 0);
            
            // Get the actual products from categories or use result.products directly
            let allProducts = [];
            if (result.categories && Array.isArray(result.categories)) {
                // If categories exist, extract products from all categories
                allProducts = result.categories.flatMap(category => category.products || []);
            } else if (result.products && Array.isArray(result.products)) {
                // If products are returned directly
                allProducts = result.products;
            }
            
            setAllProducts(allProducts); // Store unfiltered products
            setFilteredProducts(allProducts); // Initialize filtered products
            setProducts(allProducts);
            
            // Group products by category for category view
            const groupedByCategory = groupProductsByCategory(allProducts);
            setCategoryProducts(groupedByCategory);
            
            setCurrentProvince(result.current_province || '');
            setHasMore(result.hasMore || false);
            
            setPerformanceData({
                loadTime,
                fromCache: result.fromCache,
                itemCount: allProducts.length
            });
        } catch (error) {
            console.error('Error filtering by city:', error);
            endTiming(false, 0, error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMoreData = async () => {
        if (!hasMore || loadingMore) return;
        
        setLoadingMore(true);
        const nextPage = page + 1;
        
        try {
            const result = await ProductService.getUserProvinceCombined(selectedCity, nextPage, 30);
            
            // Get the actual products from categories or use result.products directly
            let newProducts = [];
            if (result.categories && Array.isArray(result.categories)) {
                // If categories exist, extract products from all categories
                newProducts = result.categories.flatMap(category => category.products || []);
            } else if (result.products && Array.isArray(result.products)) {
                // If products are returned directly
                newProducts = result.products;
            }
            
            // Merge new products with existing ones, avoiding duplicates
            setProducts(prevProducts => {
                const existingIds = new Set(prevProducts.map(p => p.id));
                const uniqueNewProducts = newProducts.filter(p => !existingIds.has(p.id));
                const updatedProducts = [...prevProducts, ...uniqueNewProducts];
                
                // Update category products as well
                const groupedByCategory = groupProductsByCategory(updatedProducts);
                setCategoryProducts(groupedByCategory);
                
                return updatedProducts;
            });
            
            setPage(nextPage);
            setHasMore(result.hasMore || false);
        } catch (error) {
            console.error('Error loading more products:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleProductClick = (productUuid, event) => {  // Changed to productUuid, added event
        // Add animation effect
        const card = event?.currentTarget;
        if (card) {
            card.classList.add('clicked');
            setTimeout(() => {
                card.classList.remove('clicked');
            }, 600);
        }
        navigate(`/products/view/${productUuid}`);
    };

    const handleCampaignClick = (campaign) => {
        if (!loggedInUser) {
            // Navigate to login page for non-logged-in users
            redirectToLogin(window.location.pathname);
        } else if (campaign.ownerId) {
            // Navigate to user's corner/shop
            navigate(`/user/corner/${campaign.ownerUuid || campaign.ownerId}/`);  // Use UUID
        } else if (campaign.link) {
            // Fallback to external link
            window.open(campaign.link, '_blank');
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };

    // Group products by category for category view
    const groupProductsByCategory = (productsList) => {
        const grouped = {};
        productsList.forEach(product => {
            const categoryName = product.category_name || 'Autres';
            if (!grouped[categoryName]) {
                // Try to get category ID from multiple sources
                let categoryId = product.category_id || product.category?.id || product.category;
                
                // If still no ID, try to find it in CategoryContext by name
                if (!categoryId && categoryList && categoryList.length > 0) {
                    const foundCategory = categoryList.find(cat => cat.name === categoryName);
                    categoryId = foundCategory ? foundCategory.id : null;
                }
                
                grouped[categoryName] = {
                    category_name: categoryName,
                    category_id: categoryId,
                    products: []
                };
            }
            grouped[categoryName].products.push(product);
        });
        
        const result = Object.values(grouped);
        
        return result;
    };

    return (
        <div>
            <div className="page-header">
                
                {/* View Mode Toggle */}
                <div className="view-mode-toggle">
                    <button 
                        className={`view-mode-btn ${viewMode === 'category' ? 'active' : ''}`}
                        onClick={() => setViewMode('category')}
                        title="Vue par catégories"
                    >
                        <i className="fas fa-th"></i>
                        <span>Catégories</span>
                    </button>
                    <button 
                        className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="Vue liste"
                    >
                        <i className="fas fa-list"></i>
                        <span>Liste</span>
                    </button>
                </div>
                
                <div className="filter-section">
                    <select
                        value={selectedCity}
                        onChange={handleCityChange}
                        className="city-filter"
                    >
                        <option value="">Toutes les villes</option>
                        {cities.map(city => (
                            <option key={city.id} value={city.id}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Add Product Filters */}
            {!loading && allProducts.length > 0 && (
                <ProductFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    productsCount={filteredProducts.length}
                    totalProducts={allProducts.length}
                />
            )}

            <div className="products-page-container">
                <h1 className="page-title">Produits à vendre à {currentProvince}</h1>

                {/* Promoted Products Section */}
                {!loadingPromoted && promotedProducts.length > 0 && userProvince && (
                    <div className="promoted-products-section">
                        <PremiumCarousel
                            products={promotedProducts}
                            loggedInUser={loggedInUser}
                            onProductClick={handleProductClick}
                            handleLikeClick={handleLikeClick}
                            isLoading={loadingPromoted}
                            title="Produits en vedette de votre province"
                            showTitle={false}
                        />
                    </div>
                )}
                {loadingPromoted && (
                    <div className="promoted-products-section">
                        <CarouselSkeleton />
                    </div>
                )}
                {loading ? (
                    <ProductGridSkeleton />
                ) : filteredProducts.length === 0 ? (
                    <div className="no-products-message modern-not-found">
                        <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                            <circle cx="32" cy="32" r="32" fill="#f3f4f6"/>
                            <path d="M20 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#c51618" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="26" cy="28" r="2" fill="#c51618"/>
                            <circle cx="38" cy="28" r="2" fill="#c51618"/>
                        </svg>
                        <h2>Aucun produit trouvé</h2>
                        <p>Essayez de changer la ville ou d’élargir votre recherche.</p>
                    </div>
                ) : viewMode === 'category' ? (
                    // Category View - Products grouped by categories
                    categoryProducts.length > 0 ? (
                        <div className="category-products-container">
                            {categoryProducts.map((categoryGroup, index) => (
                                <div key={categoryGroup.category_name} className="category-section">
                                    <div className="category-header">
                                        <div className="category-title-wrapper">
                                            <h2>{categoryGroup.category_name}</h2>
                                            <span className="product-count">
                                                {categoryGroup.products.length} produit{categoryGroup.products.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <button 
                                            className="view-more-button"
                                            onClick={() => navigate(`/products/category/${categoryGroup.category_id}/${encodeURIComponent(categoryGroup.category_name)}`)}
                                        >
                                            <i className="fas fa-eye"></i>
                                            <span>Voir plus</span>
                                        </button>
                                    </div>
                                    <ProductsGrid
                                        products={categoryGroup.products}
                                        likedProducts={likedProducts}
                                        loggedInUser={loggedInUser}
                                        handleProductClick={handleProductClick}
                                        handleLikeClick={(productId, isLikedByUser) =>
                                            handleLikeClick({
                                                productId,
                                                isLikedByUser,
                                                loggedInUser,
                                                setLikedProducts,
                                                setProducts,
                                                setNotification,
                                            })
                                        }
                                        hasMore={false}
                                        fetchMoreData={() => {}}
                                        loading={false}
                                        campaigns={campaigns}
                                        onCampaignPress={handleCampaignClick}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-products-message modern-not-found">
                            <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                                <circle cx="32" cy="32" r="32" fill="#f3f4f6"/>
                                <path d="M20 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#c51618" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="26" cy="28" r="2" fill="#c51618"/>
                                <circle cx="38" cy="28" r="2" fill="#c51618"/>
                            </svg>
                            <h2>Aucun produit trouvé</h2>
                            <p>Essayez de changer la ville ou d'élargir votre recherche.</p>
                        </div>
                    )
                ) : (
                    // List View - All products in one continuous list
                    products.length > 0 ? (
                        <ProductsGrid
                            products={products}
                            likedProducts={likedProducts}
                            loggedInUser={loggedInUser}
                            handleProductClick={handleProductClick}
                            handleLikeClick={(productId, isLikedByUser) =>
                                handleLikeClick({
                                    productId,
                                    isLikedByUser,
                                    loggedInUser,
                                    setLikedProducts,
                                    setProducts,
                                    setNotification,
                                })
                            }
                            hasMore={hasMore}
                            fetchMoreData={fetchMoreData}
                            loadingMore={loadingMore}
                            campaigns={campaigns}
                            onCampaignPress={handleCampaignClick}
                        />
                    ) : (
                        <div className="no-products-message modern-not-found">
                            <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                                <circle cx="32" cy="32" r="32" fill="#f3f4f6"/>
                                <path d="M20 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#c51618" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="26" cy="28" r="2" fill="#c51618"/>
                                <circle cx="38" cy="28" r="2" fill="#c51618"/>
                            </svg>
                            <h2>Aucun produit trouvé</h2>
                            <p>Essayez de changer la ville ou d'élargir votre recherche.</p>
                        </div>
                    )
                )}
            </div>
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
            <ScrollToTopButton />
            <PerformanceToggle />
        </div>
    );
};

export default ProductsProvince;

// Move this CSS to productsAll.css or a global stylesheet, not in the JS file!